// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBanner, Button } from '@loa/ui-components';
import { AdvisorPhotoUpload } from '../components/AdvisorPhotoUpload';
import { ProjectionAssumptionsSettings } from '../components/financialposition/ProjectionAssumptionsSettings';
import { useAdvisor } from '../contexts/AdvisorContext';
import { advisorApi } from '../api/advisorApi';
import type { BrokerageInfo } from '../types/advisor';
import './SettingsPage.css';

export function SettingsPage() {
  const navigate = useNavigate();
  const { advisor, loading, error, refetch } = useAdvisor();
  const [brokerage, setBrokerage] = useState<BrokerageInfo | null>(null);
  const [brokerageLoading, setBrokerageLoading] = useState(false);
  const [brokerageError, setBrokerageError] = useState<string | null>(null);

  useEffect(() => {
    if (!advisor?.brokerageId) return;

    const fetchBrokerage = async () => {
      setBrokerageLoading(true);
      setBrokerageError(null);
      try {
        const data = await advisorApi.getBrokerageInfo(String(advisor.brokerageId));
        setBrokerage(data);
      } catch {
        setBrokerageError('Failed to load brokerage information');
      } finally {
        setBrokerageLoading(false);
      }
    };

    fetchBrokerage();
  }, [advisor?.brokerageId]);

  const handlePhotoUpdated = () => {
    refetch();
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="settings-page__loading">
          <div className="settings-page__spinner" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="settings-page">
        <StatusBanner
          variant="error"
          message={error}
          visible
          position="inline"
          dismissible={false}
        />
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-card">
        <div className="settings-card__header">
          <h2 className="settings-card__header-title">Settings</h2>
        </div>

        <div className="settings-card__body">
          {/* Personal information */}
          <h3 className="settings-section__title">Personal information</h3>

          <div className="settings-field">
            <span className="settings-field__label">Full Name:</span>
            <span className="settings-field__value">
              {advisor?.firstName} {advisor?.lastName}
            </span>
          </div>

          <div className="settings-field">
            <span className="settings-field__label">Email:</span>
            <span className="settings-field__value">{advisor?.email}</span>
          </div>

          <div className="settings-field">
            <span className="settings-field__label">Phone:</span>
            <span className="settings-field__value">
              {advisor?.phoneNumber || 'Not provided'}
            </span>
          </div>

          <div className="settings-field">
            <span className="settings-field__label">Role:</span>
            <span className="settings-field__value">{advisor?.role}</span>
          </div>

          <div className="settings-section__divider" />

          {/* Profile photo */}
          <h3 className="settings-section__title">Profile photo</h3>

          {advisor && (
            <AdvisorPhotoUpload
              currentPhotoUrl={advisor.photoUrl}
              advisorId={advisor.id.toString()}
              onPhotoUpdated={handlePhotoUpdated}
            />
          )}

          <div className="settings-section__divider" />

          {/* Brokerage information */}
          <h3 className="settings-section__title">Brokerage information</h3>

          {brokerageLoading && (
            <div className="settings-page__loading">
              <div className="settings-page__spinner" />
            </div>
          )}

          {brokerageError && (
            <StatusBanner
              variant="error"
              message={brokerageError}
              visible
              position="inline"
              dismissible={false}
            />
          )}

          {brokerage && !brokerageLoading && (
            <div className="settings-brokerage-grid">
              <div className="settings-brokerage-grid__column">
                <div className="settings-field">
                  <span className="settings-field__label">Brokerage Name:</span>
                  <span className="settings-field__value">{brokerage.name}</span>
                </div>
                <div className="settings-field">
                  <span className="settings-field__label">Registration number:</span>
                  <span className="settings-field__value">{brokerage.registrationNumber}</span>
                </div>
                <div className="settings-field">
                  <span className="settings-field__label">Contact email:</span>
                  <span className="settings-field__value">{brokerage.contactEmail}</span>
                </div>
                <div className="settings-field">
                  <span className="settings-field__label">Phone:</span>
                  <span className="settings-field__value">{brokerage.contactPhone}</span>
                </div>
                <div className="settings-field">
                  <span className="settings-field__label">Country:</span>
                  <span className="settings-field__value">{brokerage.country}</span>
                </div>
              </div>

              <div className="settings-brokerage-grid__column">
                <div className="settings-field">
                  <span className="settings-field__label">Address line 1:</span>
                  <span className="settings-field__value">{brokerage.addressLine1}</span>
                </div>
                {brokerage.addressLine2 && (
                  <div className="settings-field">
                    <span className="settings-field__label">Address line 2:</span>
                    <span className="settings-field__value">{brokerage.addressLine2}</span>
                  </div>
                )}
                {brokerage.addressLine3 && (
                  <div className="settings-field">
                    <span className="settings-field__label">Address line 3:</span>
                    <span className="settings-field__value">{brokerage.addressLine3}</span>
                  </div>
                )}
                <div className="settings-field">
                  <span className="settings-field__label">Town/City:</span>
                  <span className="settings-field__value">{brokerage.townCity}</span>
                </div>
                <div className="settings-field">
                  <span className="settings-field__label">County:</span>
                  <span className="settings-field__value">{brokerage.stateProvinceCounty}</span>
                </div>
                <div className="settings-field">
                  <span className="settings-field__label">Eircode:</span>
                  <span className="settings-field__value">{brokerage.postalCode}</span>
                </div>
              </div>
            </div>
          )}

          <div className="settings-section__divider" />

          {/* Agency codes — moved to the Providers page */}
          <div className="settings-agency-codes__header">
            <h3 className="settings-section__title">Agency codes</h3>
            <Button
              variant="outlined"
              size="sm"
              onClick={() => navigate('/providers')}
              data-testid="go-to-providers"
            >
              Go to Providers
            </Button>
          </div>
          <p className="settings-agency-codes__empty">
            Agency codes are now managed per provider on the Providers page —
            open a provider&apos;s Settings tab to view or edit your personal
            code and the brokerage default.
          </p>
        </div>
      </div>

      <div className="settings-card">
        <div className="settings-card__header">
          <h2 className="settings-card__header-title">Projection assumptions</h2>
        </div>
        <div className="settings-card__body">
          <ProjectionAssumptionsSettings isAdmin={advisor?.role === 'ADMIN'} />
        </div>
      </div>
    </div>
  );
}
